import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { ID, Region } from "@gooner/shared";
import {
  FULL_IMAGE_SECONDS,
  PHOTO_SECONDS_STOPS,
  regionPhotoSeconds,
  regionSoundOn,
  regionVolume,
  syncLeaderOf,
} from "@gooner/shared";
import { track } from "../analytics";
import type { CollectionOption } from "../control/lib/monitorTemplates";
import {
  regionPacingLabel,
  sourceIsUnset,
  sourceLabel,
} from "../control/lib/feedLabels";
import { RegionLinkButton } from "../control/components/RegionLinkButton";
import { RegionMirrorButton } from "../control/components/RegionMirrorButton";
import { RegionTileLabels } from "../control/components/RegionTileLabels";
import { regionChromeButton } from "../control/ui/primitives";
import { IconClock, IconLoop, IconVolume, IconVolumeOff } from "../control/ui/icons";
import { useTileScale } from "./useTileScale";

/**
 * The chrome a region tile wears while hovered on the WALL: the same name badge and
 * feed pill the editor's canvas draws (RegionTileLabels — one vocabulary, two
 * surfaces), plus a top-right row of the per-region controls that make sense
 * mid-watch — sound, photo time, sync, loop.
 *
 * Layering contract with the rest of the tile: the hover ring is z-20
 * pointer-events-none, the fap crown owns BOTTOM-right at z-30, and the burst layer is
 * z-40 pointer-events-none — so this chrome takes top-left (labels, inert) and
 * TOP-right (controls, z-30) and stays out of the crown's corner.
 *
 * Every control stops propagation — the tile div underneath treats a click as "open
 * this region's feed popup" — and blurs itself after a click, or the next Space
 * (skip-all) would be swallowed by the focused button as a synthesized click.
 *
 * A follower's sound, photo time and loop land on its LEADER, the one region that
 * actually runs the feed — the same routing the fap overlay and the clip-lock use. The link button
 * is the exception on purpose: linking/unlinking is about THIS tile's relationship.
 */
export function RegionHoverChrome({
  region,
  regions,
  index,
  collections,
  screenMuted,
  syncTint,
  onEditRegion,
  onToggleLoop,
  onLink,
  onUnlink,
  onToggleMirror,
  onLockChange,
  onCandidateHover,
  topInset = 0,
}: {
  /** Pixels to push the whole chrome down — the host's answer to the Now Showing chip
   *  floating over a top-row tile's corners (see chipCovers in ScreenThumbBar). */
  topInset?: number;
  /** The tile's own region (possibly a follower). */
  region: Region;
  /** The AUTHORED region list (view.regions), for leader resolution and link targets. */
  regions: Region[];
  /** Position in the authored list, for the "Region N" fallback name. */
  index: number;
  collections: CollectionOption[];
  /** The master mute — only for honest tooltips; the toggle saves regardless. */
  screenMuted: boolean;
  syncTint?: string | null;
  /** Persist a sound or photo-time patch onto the LEADER region (already resolved by
   *  this chrome). */
  onEditRegion: (
    leaderId: ID,
    patch: { soundOn?: boolean; volume?: number; photoSeconds?: number },
    opts?: { debounce?: number },
  ) => void;
  /** Lock/unlock this region's playback to the clip on screen (routes to the leader). */
  onToggleLoop: (regionId: ID) => void;
  /** Sync THIS region to the target. */
  onLink: (targetId: ID) => void;
  onUnlink: () => void;
  /** Flip/unflip the mirrored playback of this region's sync (toggleMirror). */
  onToggleMirror: () => void;
  /**
   * A sub-interaction is live (link menu open, slider mid-drag): the host must keep
   * this chrome mounted even when the pointer leaves the tile — the link menu portals
   * BELOW the tile and a drag strays past its edge, and either would otherwise
   * unmount the very control under the pointer.
   */
  onLockChange: (locked: boolean) => void;
  /** Link-menu candidate under the pointer (null = none) — see RegionLinkButton. */
  onCandidateHover?: (id: ID | null) => void;
}) {
  const leader = syncLeaderOf(region, regions);
  const shared = leader ?? region;
  const soundOn = regionSoundOn(shared);
  const volume = regionVolume(shared);
  const looping = shared.source.kind === "clip";
  const photoSeconds = regionPhotoSeconds(shared);
  // One volume event per DRAG, at its settle — pointerup and lostpointercapture both
  // end a drag and often both fire, so the flag makes them one report, carrying the
  // level the hand actually left it at (a number, never a word).
  const volumeDragRef = useRef(false);
  const endVolumeDrag = () => {
    // An open Photo time panel holds the lock on its own account.
    onLockChange(photoOpenRef.current);
    if (!volumeDragRef.current) return;
    volumeDragRef.current = false;
    track("region_volume_set", { level: Math.round(regionVolume(shared) * 100) });
  };

  /*
   * PHOTO TIME. A chip in the button row showing the hold ("6s"); pressing it opens a
   * panel under the row with the slider. A panel and not an always-on slider because
   * the row already carries the volume slider, and two unlabeled faders side by side
   * on a tile are a guess which is which. While open it pins the chrome exactly like
   * the link menu (a drag strays, a finger lifts), and closes on an outside press or
   * Escape the same way.
   *
   * The slider moves over PHOTO_SECONDS_STOPS by INDEX, so every position is a stop.
   * Like volume, each move folds into the view at once (the tile re-times the photo on
   * screen live — see RegionPlayer) and only the PUT waits for the hand to settle; and
   * one event per settle, carrying where it started as well as where it landed.
   */
  const [photoOpen, setPhotoOpenState] = useState(false);
  const photoOpenRef = useRef(false);
  const setPhotoOpen = (open: boolean) => {
    if (photoOpenRef.current === open) return;
    photoOpenRef.current = open;
    setPhotoOpenState(open);
    onLockChange(open);
  };
  const photoFromRef = useRef<number | null>(null);
  const beginPhotoEdit = () => {
    if (photoFromRef.current === null) photoFromRef.current = photoSeconds;
  };
  const endPhotoEdit = () => {
    const from = photoFromRef.current;
    photoFromRef.current = null;
    if (from === null || from === photoSeconds) return;
    track("region_photo_time_set", { seconds: photoSeconds, from_seconds: from });
  };
  const setPhotoSeconds = (seconds: number, debounce?: number) =>
    onEditRegion(shared.id, { photoSeconds: seconds }, debounce ? { debounce } : undefined);
  useEffect(() => {
    if (!photoOpen) return;
    // Capture phase, as the link menu does: the tile under the pointer stops
    // pointerdown propagation. Inside the control row (chip, panel) is not "outside".
    const close = (e: PointerEvent) => {
      if (controlsRef.current?.contains(e.target as Node)) return;
      setPhotoOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      setPhotoOpen(false);
    };
    document.addEventListener("pointerdown", close, true);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("pointerdown", close, true);
      document.removeEventListener("keydown", onKey, true);
    };
    // setPhotoOpen funnels through a ref; only the panel's presence matters here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photoOpen]);
  // Unmounted open (the view swapped under it): hand the lock back.
  useEffect(
    () => () => {
      if (photoOpenRef.current) onLockChange(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  /*
   * SIZE WITH THE TILE. The label cluster is shared with the editor's canvas, where a
   * tile is postcard-sized and 10px is right — but on the wall a region can be half a
   * television, and chrome that stays postcard-sized reads as lint from the sofa. So
   * the wall scales its two clusters by the tile's rendered short side (useTileScale —
   * the same rule the fap crown grows by, so the wall's controls grow together). A
   * transform rather than font juggling, so the shared component stays untouched and
   * buttons/pills/slider all grow together; the labels wrapper gets the inverse width
   * so "100%" still means the tile's edge and a long keyword list truncates there
   * instead of spilling past it.
   */
  const measureRef = useRef<HTMLDivElement>(null);
  /*
   * NEVER SMALLER THAN DESIGNED (2026-10-06, Casey on a 393px phone). The tile scale
   * only GROWS this chrome now. Shrinking it for a small tile — a 3×4 wall on a phone
   * is ~130px a tile, so 0.7 — took the coarse-pointer buttons from 34px to 24px and
   * the labels to 7px text: hit targets under a fingertip and words nobody can read.
   */
  const chromeScale = Math.max(1, useTileScale(measureRef));
  const controlsRef = useRef<HTMLDivElement>(null);
  /*
   * A NARROW TILE STACKS. Where the widest label and the control row cannot sit side by
   * side, the controls keep the top-right corner and the labels start under them rather
   * than being painted over. Measured, not guessed, so the rule follows the control row
   * as it grows or wraps (its slider drops under the buttons — see below) and the labels
   * as the feed changes.
   */
  const [labelsDrop, setLabelsDrop] = useState(0);
  useLayoutEffect(() => {
    const tile = measureRef.current?.parentElement;
    const controls = controlsRef.current;
    if (!tile || !controls) return;
    const labels = measureRef.current;
    const measure = () => {
      // The widest label at its natural (untruncated) width, in on-screen pixels.
      let labelW = 0;
      for (const el of labels?.querySelectorAll("span") ?? []) {
        labelW = Math.max(labelW, el.scrollWidth * chromeScale);
      }
      const c = controls.getBoundingClientRect();
      const collide = tile.clientWidth - 8 < c.width + labelW + 8;
      setLabelsDrop(collide ? c.height + 4 : 0);
    };
    const ro = new ResizeObserver(measure);
    ro.observe(tile);
    ro.observe(controls);
    if (labels) ro.observe(labels);
    measure();
    return () => ro.disconnect();
  }, [chromeScale]);

  /** How long a slider drag's PUT may lag the last pointer move. The screen state is
   *  folded per move; only the request waits for the hand to settle. */
  const VOLUME_PUT_DEBOUNCE_MS = 400;

  // One helper for every button in this row — see regionChromeButton's header for why
  // it cannot live in three files.
  const iconButton = regionChromeButton;

  return (
    <>
      {/* The same labels the editor's canvas draws, from the same component and the
          same vocabulary — a follower's tile tells the truth (the leader's feed), and
          the badge wears its sync group's tint. Inert: RegionTileLabels is
          pointer-events-none, so it never eats the click that opens the popup. */}
      <div
        ref={measureRef}
        className="absolute inset-x-0 z-30"
        style={{
          top: topInset + labelsDrop,
          transform: `scale(${chromeScale})`,
          transformOrigin: "top left",
          width: `${100 / chromeScale}%`,
        }}
      >
        <RegionTileLabels
          name={region.name}
          index={index}
          meta={{
            feed: sourceLabel(shared.source, collections),
            timer: leader ? null : regionPacingLabel(region),
            unset: sourceIsUnset(shared.source),
          }}
          syncTint={syncTint}
        />
      </div>

      <div
        ref={controlsRef}
        /* `flex-wrap-reverse` with TWO items — the slider and the button group — so a
           tile wide enough keeps the one row it always had (slider left of the icons),
           and a narrow one puts the buttons on top and the slider under them, right-
           aligned, instead of running the slider off the tile's left edge. The max width
           is the tile less the inset, in pre-scale pixels. */
        className="absolute right-1 z-30 flex flex-wrap-reverse items-center justify-end gap-1"
        style={{
          top: 4 + topInset,
          maxWidth: `calc((100% - 8px) / ${chromeScale})`,
          transform: `scale(${chromeScale})`,
          transformOrigin: "top right",
        }}
        // The tile underneath opens the region popup on click and starts nothing on
        // pointerdown — but every control here means itself, not the tile.
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
      >
        {/* The Photo time panel, FIRST so that `flex-wrap-reverse` lays it on its own
            line BELOW the buttons. `contain: inline-size` keeps its text from sizing the
            row: the panel takes the width the buttons already set and wraps inside it,
            instead of stretching the whole cluster across the tile. */}
        {photoOpen ? (
          <div
            role="group"
            aria-label="Photo time"
            style={{ contain: "inline-size" }}
            className="basis-full rounded-md bg-[var(--color-ink)]/80 px-2 py-1.5 text-[10px] text-zinc-200 backdrop-blur-sm"
          >
            <div className="flex items-center justify-between gap-2">
              <span>
                Photo time{" "}
                <span className="font-semibold text-[var(--color-accent-soft)]">{photoSeconds}s</span>
                {photoSeconds === FULL_IMAGE_SECONDS ? (
                  <span className="text-zinc-400"> (default)</span>
                ) : null}
              </span>
              {photoSeconds !== FULL_IMAGE_SECONDS ? (
                <button
                  type="button"
                  className="text-zinc-300 underline hover:text-white"
                  onClick={(e) => {
                    e.currentTarget.blur();
                    track("region_photo_time_set", {
                      seconds: FULL_IMAGE_SECONDS,
                      from_seconds: photoSeconds,
                    });
                    setPhotoSeconds(FULL_IMAGE_SECONDS);
                  }}
                >
                  Reset to {FULL_IMAGE_SECONDS} s
                </button>
              ) : null}
            </div>
            <input
              type="range"
              min={0}
              max={PHOTO_SECONDS_STOPS.length - 1}
              step={1}
              value={Math.max(0, PHOTO_SECONDS_STOPS.indexOf(photoSeconds as never))}
              aria-label="Photo time"
              aria-valuetext={`${photoSeconds} seconds`}
              title="How long each photo stays before this tile moves on"
              // touch-none + coarse hit height: the same reasons as the volume fader.
              className="my-1 block h-1 w-full min-w-28 cursor-pointer touch-none accent-[var(--color-accent)] pointer-coarse:h-6"
              onChange={(e) => {
                beginPhotoEdit();
                const s = PHOTO_SECONDS_STOPS[Number(e.currentTarget.value)];
                if (s !== undefined) setPhotoSeconds(s, VOLUME_PUT_DEBOUNCE_MS);
              }}
              onPointerDown={(e) => {
                e.stopPropagation();
                beginPhotoEdit();
              }}
              onPointerUp={endPhotoEdit}
              onLostPointerCapture={endPhotoEdit}
              onKeyUp={endPhotoEdit}
              onBlur={endPhotoEdit}
            />
            <div className="flex justify-between text-zinc-400">
              <span>{PHOTO_SECONDS_STOPS[0]}s</span>
              <span>{PHOTO_SECONDS_STOPS[PHOTO_SECONDS_STOPS.length - 1]}s</span>
            </div>
            <p className="mt-0.5 text-zinc-400">Photos in this tile. Videos still play to the end.</p>
          </div>
        ) : null}

        {/* The slider leads the row so it grows LEFTWARD of the icons and never pushes
            them. Only while sound is on: a level for a silent region is a dial wired to
            nothing. Value is the live region prop — edits fold into the view state per
            move, only the PUT is debounced — so it never fights the drag. */}
        {soundOn ? (
          <input
            type="range"
            min={0}
            max={100}
            value={Math.round(volume * 100)}
            aria-label="Region volume"
            title="This region's volume"
            /*
             * `touch-none` for the same reason the master and radio faders carry it: the
             * wall is a touch surface, and a horizontal drag on a 4px-tall control is
             * otherwise ambiguous with a pan — the browser resolves it by scrolling and
             * the level never moves. This slider was the one fader in the app without it.
             *
             * `pointer-coarse:h-6` gives the thumb something to land on. The TRACK stays
             * thin; the extra height is hit area, which is the part a finger needs and the
             * part a mouse does not.
             */
            className="h-1 w-20 max-w-full cursor-pointer touch-none accent-[var(--color-accent)] pointer-coarse:h-6 pointer-coarse:w-24"
            onChange={(e) =>
              onEditRegion(
                shared.id,
                { volume: Number(e.currentTarget.value) / 100 },
                { debounce: VOLUME_PUT_DEBOUNCE_MS },
              )
            }
            // The drag pins the chrome (see onLockChange). lostpointercapture is the
            // net under pointerup: a range input implicitly captures the pointer, and
            // a drag that ends off-window fires no pointerup here.
            onPointerDown={(e) => {
              e.stopPropagation();
              volumeDragRef.current = true;
              onLockChange(true);
            }}
            onPointerUp={endVolumeDrag}
            onLostPointerCapture={endVolumeDrag}
          />
        ) : null}

        <div className="flex shrink-0 items-center gap-1">
        <button
          type="button"
          aria-pressed={soundOn}
          title={
            soundOn
              ? screenMuted
                ? "Sound on for this region — unmute the screen (bar button) to hear it"
                : "Turn sound off for this region"
              : "Turn sound on for this region"
          }
          className={iconButton(soundOn)}
          onClick={(e) => {
            // Focus dropped so the next Space skips (this hovered tile) instead of
            // re-toggling the button.
            e.currentTarget.blur();
            track("region_sound_set", { on: !soundOn });
            // Only the switch travels; the region's kept `volume` comes back with it.
            onEditRegion(shared.id, { soundOn: !soundOn });
          }}
        >
          {soundOn ? (
            <IconVolume width={14} height={14} />
          ) : (
            <IconVolumeOff width={14} height={14} />
          )}
        </button>

        <button
          type="button"
          aria-expanded={photoOpen}
          aria-label={`Photo time, ${photoSeconds} seconds`}
          title="How long each photo stays before this tile moves on"
          // Lit only when it says something other than the default.
          className={`${iconButton(photoSeconds !== FULL_IMAGE_SECONDS)} flex items-center gap-0.5 text-[11px] leading-[14px] tabular-nums`}
          onClick={(e) => {
            e.currentTarget.blur();
            setPhotoOpen(!photoOpenRef.current);
          }}
        >
          <IconClock width={14} height={14} />
          {photoSeconds}s
        </button>

        {/* Only while synced — RegionMirrorButton renders nothing on a lone region. */}
        <RegionMirrorButton
          region={region}
          allRegions={regions}
          onToggle={onToggleMirror}
        />

        <RegionLinkButton
          region={region}
          allRegions={regions}
          onLink={onLink}
          onUnlink={onUnlink}
          // The menu portals below the tile — pin the chrome while it is open.
          onMenuToggle={onLockChange}
          onCandidateHover={onCandidateHover}
        />

        <button
          type="button"
          aria-pressed={looping}
          title={
            looping
              ? "Stop looping — back to this region's feed"
              : "Loop the clip that's playing, forever"
          }
          className={iconButton(looping)}
          onClick={(e) => {
            e.currentTarget.blur();
            onToggleLoop(region.id);
          }}
        >
          <IconLoop width={14} height={14} />
        </button>
        </div>
      </div>
    </>
  );
}
